/**
 * =========================================================
 * LIDAH COLLECTION AUDIO PLAYER
 * =========================================================
 *
 * Regelt het afspelen van geluidsfragmenten op productkaarten.
 *
 * Functionaliteit:
 * - Speelt audio af wanneer op de luisterknop wordt geklikt
 * - Laat maximaal één geluidsfragment tegelijk afspelen
 * - Stopt het vorige fragment wanneer een ander wordt gestart
 * - Wisselt de knop tussen afspelen en pauzeren
 * - Zet de knop terug wanneer de audio afgelopen is
 *
 * Gebruikte HTML-attributen:
 * - data-lidah-audio
 * - data-audio-src
 * - data-play-label
 * - data-pause-label
 * - data-audio-label
 *
 * Dit bestand wordt geladen vanuit layout/theme.liquid.
 * =========================================================
 */

(() => {
  /**
   * Voorkomt dat deze functionaliteit meerdere keren
   * wordt geïnitialiseerd.
   */
  if (window.LidahCollectionAudioInitialized) return;

  window.LidahCollectionAudioInitialized = true;

  /**
   * Houdt bij welk audiofragment en welke knop
   * momenteel actief zijn.
   */
  let activeAudio = null;
  let activeButton = null;

  /**
   * Past de visuele status en toegankelijkheidsstatus
   * van een audioknop aan.
   *
   * @param {HTMLElement|null} button
   * @param {boolean} isPlaying
   */
  function setButtonState(button, isPlaying) {
    if (!button) return;

    button.classList.toggle("is-playing", isPlaying);

    button.setAttribute(
      "aria-pressed",
      isPlaying ? "true" : "false"
    );

    const label = button.querySelector("[data-audio-label]");

    if (label) {
      label.textContent = isPlaying
        ? button.dataset.pauseLabel
        : button.dataset.playLabel;
    }
  }

  /**
   * Stopt het actieve geluidsfragment,
   * zet het terug naar het begin en reset de knop.
   */
  function stopActiveAudio() {
    if (activeAudio) {
      activeAudio.pause();
      activeAudio.currentTime = 0;
    }

    setButtonState(activeButton, false);

    activeAudio = null;
    activeButton = null;
  }

  /**
   * Luistert naar klikken op audioknoppen.
   *
   * Event delegation wordt gebruikt, zodat de functie
   * ook blijft werken wanneer Shopify productkaarten
   * dynamisch opnieuw laadt.
   */
  document.addEventListener("click", async (event) => {
    const target =
      event.target instanceof Element
        ? event.target
        : null;

    const button = target?.closest("[data-lidah-audio]");

    /**
     * Stop wanneer de klik niet afkomstig is
     * van een LIDAH-audioknop.
     */
    if (!button) return;

    /**
     * Voorkomt dat de klik ook een eventuele
     * productlink of andere bovenliggende actie activeert.
     */
    event.preventDefault();
    event.stopPropagation();

    /**
     * Wanneer dezelfde knop opnieuw wordt aangeklikt,
     * wisselen we tussen pauzeren en verder afspelen.
     */
    if (activeButton === button && activeAudio) {
      if (activeAudio.paused) {
        try {
          await activeAudio.play();
          setButtonState(button, true);
        } catch (error) {
          stopActiveAudio();
        }
      } else {
        activeAudio.pause();
        setButtonState(button, false);
      }

      return;
    }

    /**
     * Stop eerst een eventueel ander geluidsfragment.
     */
    stopActiveAudio();

    /**
     * Lees de URL van het audiobestand uit
     * het data-audio-src-attribuut van de knop.
     */
    const audioUrl = button.dataset.audioSrc;

    if (!audioUrl) return;

    /**
     * Maak een nieuw audio-object aan.
     *
     * preload = "none" voorkomt dat alle audiobestanden
     * direct bij het openen van de collectiepagina laden.
     */
    const audio = new Audio();

    audio.preload = "none";
    audio.src = audioUrl;

    activeAudio = audio;
    activeButton = button;

    /**
     * Reset de knop wanneer het geluidsfragment
     * volledig is afgespeeld.
     */
    audio.addEventListener(
      "ended",
      stopActiveAudio,
      { once: true }
    );

    /**
     * Reset de knop wanneer het audiobestand
     * niet correct kan worden geladen.
     */
    audio.addEventListener(
      "error",
      stopActiveAudio,
      { once: true }
    );

    /**
     * Probeer het geluidsfragment af te spelen.
     */
    try {
      await audio.play();
      setButtonState(button, true);
    } catch (error) {
      /**
       * Bijvoorbeeld wanneer de browser het afspelen
       * blokkeert of het bestand niet bereikbaar is.
       */
      stopActiveAudio();
    }
  });
})();